/**
 * SPIKE (claude/opencode-framework-eval): opencode FrameworkAdapter.
 *
 * A working proof-of-concept adapter for https://opencode.ai as an Augmented
 * runtime, sitting alongside the claude-code adapter behind the same
 * `FrameworkAdapter` seam. Scope is deliberately a spike:
 *
 *   IMPLEMENTED (the mapping proof)
 *     - buildArtifacts → AGENTS.md, opencode.json, CHARTER.md, TOOLS.md
 *     - drift tracking, agent registration markers, auth-profile .env writes
 *     - MCP add/remove + a minimal channel-credential path (opencode `mcp` shape)
 *
 *   DONE since the initial spike (see README.md in this dir)
 *     - Widening the `FrameworkId` union + relaxing the DB CHECK constraint
 *     - The inbound channel rail: validated against a live `opencode serve`
 *       (inbound-bridge.ts / inbound-watcher.ts / opencode-client.ts) — Claude
 *       Code's `notifications/claude/channel` has no opencode equivalent, so the
 *       bridge drives the headless server via `session.prompt` instead.
 *
 *   NOT YET (productionization — see README.md in this dir)
 *     - The full channel sender-gating / peer-roster options bag
 *     - Scheduling (opencode has no cron; `opencode serve` + SDK would drive it)
 *     - Manager supervision of the per-agent container (ADR-0047)
 *
 * The adapter self-registers on import (module side effect), matching the
 * claude-code adapter's contract.
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync, readdirSync, rmSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

import type { FrameworkAdapter, ProvisionArtifact, AuthProfileInput } from '../../framework-adapter.js';
import { registerFramework } from '../../framework-registry.js';
import type { ProvisionInput } from '../../types.js';
import { isDeprecatedFramework, modelSlotsForFramework } from '../../../types/agent.js';
import type { ScheduledTaskRow } from '../../../types/scheduled-task.js';
import type { ResolvedIntegration } from '../../../types/integration.js';
import { buildChannelServerEnv, buildChannelCredentialEnv, type ChannelEnvOptions } from '../../channel-env.js';
import { buildOpencodeConfig, MCP_BUNDLE_BASENAME } from './config.js';
import { generateAgentsMd } from './identity.js';
import { buildOpencodeIntegrationServers } from './integrations.js';

const SCHEDULES_FILE = 'opencode-schedules.json';

const FRAMEWORK_ID = 'opencode';
const CONFIG_FILE = 'opencode.json';
const VALID_CODE_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Per-channel MCP server bundle files (shared with the claude-code runtime).
const CHANNEL_SERVER_FILES: Record<string, string> = {
  slack: 'slack-channel.js',
  telegram: 'telegram-channel.js',
  msteams: 'teams-channel.js',
  whatsapp: 'whatsapp-channel.js',
};

function assertValidCodeName(codeName: string): void {
  if (!VALID_CODE_NAME.test(codeName)) {
    throw new Error(`Invalid agent code_name: "${codeName}". Must be kebab-case.`);
  }
}

function getHomeDir(): string {
  return process.env['HOME'] ?? process.env['USERPROFILE'] ?? homedir();
}

/** Per-agent root config dir: ~/.augmented/{codeName}/ (shared with claude-code). */
function agentDir(codeName: string): string {
  assertValidCodeName(codeName);
  return join(getHomeDir(), '.augmented', codeName);
}

/**
 * Config dir the serve reads: ~/.augmented/{codeName}/provision/
 *
 * ENG-7959: the manager writes the provisioned base `opencode.json` here and
 * points `opencode serve`'s cwd here (ADR-0047), and the runtime's materializer
 * reads `provision/opencode.json` to derive the global MCP config the serve
 * loads at boot (ENG-7956). Incremental MCP writes (integrations, channels) MUST
 * land in the SAME file, or they never reach the serve. This previously pointed
 * at a sibling `project/` dir that nothing downstream read, so an opencode
 * agent's integration/channel MCP servers were silently orphaned.
 */
function provisionConfigDir(codeName: string): string {
  return join(agentDir(codeName), 'provision');
}

function mcpBundlePath(): string {
  return join(getHomeDir(), '.augmented', '_mcp', MCP_BUNDLE_BASENAME);
}

function configPath(codeName: string): string {
  return join(provisionConfigDir(codeName), CONFIG_FILE);
}

function readConfig(codeName: string): Record<string, unknown> {
  const p = configPath(codeName);
  if (!existsSync(p)) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(p, 'utf8'));
  } catch {
    return {};
  }
  // A file holding `null`, `[]`, or a bare scalar PARSES fine, so the try/catch
  // above doesn't catch it — and every caller does `readConfig(x)['key']`, which
  // throws on the null. Honour the declared return type: anything that isn't a
  // JSON object reads as "no config" (CodeRabbit, PR #3712).
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  return parsed as Record<string, unknown>;
}

function writeConfig(codeName: string, config: Record<string, unknown>): void {
  const p = configPath(codeName);
  mkdirSync(provisionConfigDir(codeName), { recursive: true });
  writeFileSync(p, JSON.stringify(config, null, 2));
}

/**
 * Upsert KEY=VALUE lines into `<agentDir>/.env.integrations` (0600). The manager
 * sources this file into the `opencode serve` process env, so opencode's
 * `{env:VAR}` substitution resolves the referenced secrets at config-read time
 * (the opencode analogue of Claude Code's Track-D `.env.integrations` + `${VAR}`).
 */
function upsertEnvIntegrations(codeName: string, updates: Record<string, string>): void {
  if (Object.keys(updates).length === 0) return;
  const p = join(agentDir(codeName), '.env.integrations');
  const lines = new Map<string, string>();
  if (existsSync(p)) {
    for (const line of readFileSync(p, 'utf8').split('\n')) {
      const eq = line.indexOf('=');
      if (eq > 0) lines.set(line.slice(0, eq), line.slice(eq + 1));
    }
  }
  for (const [k, v] of Object.entries(updates)) lines.set(k, v);
  mkdirSync(agentDir(codeName), { recursive: true });
  writeFileSync(p, `${[...lines.entries()].map(([k, v]) => `${k}=${v}`).join('\n')}\n`, { mode: 0o600 });
}

/**
 * Parse `<agentDir>/.env.integrations` back into a map. Deliberately mirrors
 * `upsertEnvIntegrations`'s raw `KEY=VALUE` format (no shell quoting) rather
 * than reusing the core env-file model, which quotes: these two functions are
 * the only writer/reader of THIS file and must agree with each other.
 */
function readEnvIntegrations(codeName: string): Record<string, string> {
  const p = join(agentDir(codeName), '.env.integrations');
  if (!existsSync(p)) return {};
  const out: Record<string, string> = {};
  for (const line of readFileSync(p, 'utf8').split('\n')) {
    const eq = line.indexOf('=');
    if (eq > 0) out[line.slice(0, eq)] = line.slice(eq + 1);
  }
  return out;
}

/**
 * Sidecar recording the `mcp` server keys `writeIntegrations` owns, so a later
 * cycle can prune a disconnected integration's server without touching the
 * `augmented` entry, channel servers, or managed-toolkit servers (which the
 * manager reconciles on their own separate paths). Kept out of `opencode.json`
 * itself so the drift-checked config stays free of Augmented-only bookkeeping.
 */
const INTEGRATION_KEYS_FILE = 'integration-mcp-keys.json';

function readIntegrationServerKeys(codeName: string): string[] {
  const p = join(agentDir(codeName), INTEGRATION_KEYS_FILE);
  if (!existsSync(p)) return [];
  try {
    const parsed = JSON.parse(readFileSync(p, 'utf8')) as unknown;
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function writeIntegrationServerKeys(codeName: string, keys: string[]): void {
  mkdirSync(agentDir(codeName), { recursive: true });
  writeFileSync(
    join(agentDir(codeName), INTEGRATION_KEYS_FILE),
    JSON.stringify([...keys].sort(), null, 2),
  );
}

/** A lone `{env:VAR}` substitution, opencode's analogue of Claude Code's `${VAR}`. */
const ENV_SUBSTITUTION = /^\{env:([A-Za-z_][A-Za-z0-9_]*)\}$/;

/**
 * Read back a channel MCP server's fully-resolved env - the inverse of
 * `writeChannelCredentials`.
 *
 * On Claude Code the channel MCP runs in-process and opencode's `{env:VAR}`
 * substitution resolves its own secrets at config-read time. opencode has no
 * inbound channel push primitive, so the MANAGER has to make the same gating
 * decision before injecting a turn (ADR-0047), which means it needs the same env
 * the channel server would have seen. Rather than re-derive it (the refresh
 * path's ChannelEnvOptions derivation is long and shared with the live Claude
 * fleet), this reads back exactly what provisioning wrote and resolves the
 * secret refs from `.env.integrations`. Reading the written artifact keeps one
 * source of truth and survives a manager restart, since both files are on disk.
 *
 * Returns null when the channel has no MCP entry (never provisioned / removed).
 *
 * An UNRESOLVABLE `{env:VAR}` ref drops its key rather than passing the literal
 * `"{env:VAR}"` through: a caller treating that string as a bot token would
 * present it to Slack as a bearer credential and get a baffling `invalid_auth`,
 * where an absent key fails the caller's own "not configured" check cleanly.
 */
export function readChannelServerEnv(
  codeName: string,
  channelId: string,
): Record<string, string> | null {
  const mcp = readConfig(codeName)['mcp'] as Record<string, unknown> | undefined;
  const entry = mcp?.[channelId] as { environment?: Record<string, string> } | undefined;
  if (!entry?.environment) return null;

  const secrets = readEnvIntegrations(codeName);
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(entry.environment)) {
    const ref = ENV_SUBSTITUTION.exec(raw);
    if (!ref) {
      out[key] = raw;
      continue;
    }
    const resolved = secrets[ref[1]!] ?? process.env[ref[1]!];
    if (resolved !== undefined) out[key] = resolved;
  }
  return out;
}

/** Coerce a Claude-Code-style MCP entry into opencode's `mcp` shape. */
function toOpencodeMcpEntry(
  config:
    | { command: string; args?: string[]; env?: Record<string, string> }
    | { url: string; headers?: Record<string, string>; type?: 'http' | 'sse' },
): Record<string, unknown> {
  if ('url' in config) {
    return {
      type: 'remote',
      url: config.url,
      ...(config.headers ? { headers: config.headers } : {}),
      enabled: true,
    };
  }
  return {
    type: 'local',
    command: [config.command, ...(config.args ?? [])],
    ...(config.env ? { environment: config.env } : {}),
    enabled: true,
  };
}

export const opencodeAdapter: FrameworkAdapter = {
  id: FRAMEWORK_ID,
  label: 'opencode',
  cliBinary: 'opencode',
  // Sourced from the canonical map; unknown ids read as not-deprecated, so this
  // is false until (if ever) opencode is added to FRAMEWORK_DEPRECATION.
  deprecated: isDeprecatedFramework(FRAMEWORK_ID),
  // ENG-9485: opencode fills ONE model slot. buildOpencodeConfig emits a single
  // `model` + `provider` pair and has no small-fast concept, so a policy's
  // `small_fast` row is correctly not consumed here rather than being an error.
  modelSlots: modelSlotsForFramework(FRAMEWORK_ID),

  getAgentDir(codeName: string): string {
    return agentDir(codeName);
  },

  buildArtifacts(input: ProvisionInput): ProvisionArtifact[] {
    const config = buildOpencodeConfig(input, { mcpBundlePath: mcpBundlePath() });
    return [
      { relativePath: 'AGENTS.md', content: generateAgentsMd(input) },
      { relativePath: CONFIG_FILE, content: JSON.stringify(config, null, 2) },
      // Governance docs carried verbatim, same as the claude-code adapter.
      { relativePath: 'CHARTER.md', content: input.charterContent },
      { relativePath: 'TOOLS.md', content: input.toolsContent },
    ];
  },

  driftTrackedFiles(): string[] {
    return ['AGENTS.md', CONFIG_FILE, 'CHARTER.md', 'TOOLS.md'];
  },

  getMcpPath(codeName: string): string | null {
    return configPath(codeName);
  },

  async getRegisteredAgents(): Promise<Set<string>> {
    const root = join(getHomeDir(), '.augmented');
    if (!existsSync(root)) return new Set();
    const registered = new Set<string>();
    for (const entry of readdirSync(root, { withFileTypes: true })) {
      if (!entry.isDirectory()) continue;
      if (existsSync(join(root, entry.name, 'registration.json'))) registered.add(entry.name);
    }
    return registered;
  },

  async registerAgent(codeName: string): Promise<boolean> {
    mkdirSync(agentDir(codeName), { recursive: true });
    writeFileSync(
      join(agentDir(codeName), 'registration.json'),
      JSON.stringify({ code_name: codeName, framework: FRAMEWORK_ID }, null, 2),
    );
    return true;
  },

  async deregisterAgent(codeName: string): Promise<boolean> {
    const marker = join(agentDir(codeName), 'registration.json');
    if (existsSync(marker)) rmSync(marker);
    return true;
  },

  writeAuthProfiles(codeName: string, profiles: AuthProfileInput[]): void {
    mkdirSync(agentDir(codeName), { recursive: true });
    const lines = profiles
      .filter((p) => p.api_key)
      .map((p) => `${p.provider.toUpperCase()}_API_KEY=${p.api_key}`);
    if (lines.length === 0) return;
    // 0o600 — the file holds provider API keys; keep it owner-only like
    // .env.integrations, never world-readable.
    writeFileSync(join(agentDir(codeName), '.env'), `${lines.join('\n')}\n`, { mode: 0o600 });
  },

  writeMcpServer(codeName, serverId, config): void {
    const cfg = readConfig(codeName);
    const mcp = (cfg['mcp'] as Record<string, unknown>) ?? {};
    mcp[serverId] = toOpencodeMcpEntry(config);
    cfg['mcp'] = mcp;
    writeConfig(codeName, cfg);
  },

  /**
   * ENG-7976: render org/team/agent-scoped integrations into opencode's `mcp`
   * block. Follow-up to ENG-7959 - the adapter previously implemented no
   * `writeIntegrations`, so the manager's `/host/agent-integrations` provisioning
   * path was silently skipped for opencode and every integration MCP server
   * (remote_mcp catalog + OAuth remotes + data-driven native) was dropped.
   *
   * The mapping is pure (`buildOpencodeIntegrationServers`, reusing the same
   * shared builders as the claude-code adapter). Secrets never enter the
   * drift-checked config: they are seeded into `.env.integrations` and referenced
   * as `{env:VAR}`, resolved by the runtime materializer at serve spawn - the same
   * contract `writeChannelCredentials` uses. Integration servers are merged INTO
   * the existing `mcp` map (never a wholesale replace) so the generator's
   * `augmented` entry and any channel servers already on disk survive.
   *
   * Stale integration servers (an integration disconnected since the last write)
   * are pruned: any entry previously written by this method that is not in the
   * fresh set is removed, while the `augmented` entry, channel servers and managed
   * toolkit servers are left untouched.
   */
  writeIntegrations(codeName: string, integrations: ResolvedIntegration[], agentId?: string): void {
    const { servers, envUpdates } = buildOpencodeIntegrationServers(integrations, {
      agentId: agentId ?? '',
      agentCodeName: codeName,
    });

    // Secrets first, so a `{env:VAR}` reference in the servers below always has
    // its value present in `.env.integrations` before the serve reads the config.
    upsertEnvIntegrations(codeName, envUpdates);

    const previous = readIntegrationServerKeys(codeName);

    // Nothing to add and nothing we previously owned to prune: don't fabricate a
    // bare, model-less opencode.json for an agent whose base config hasn't been
    // generated yet. (The manager always provisions the base config first and
    // only calls this with a non-empty list, so this guards standalone/edge use.)
    if (Object.keys(servers).length === 0 && previous.length === 0 && !existsSync(configPath(codeName))) {
      return;
    }

    const cfg = readConfig(codeName);
    const mcp = (cfg['mcp'] as Record<string, unknown>) ?? {};

    // Prune integration servers we wrote on a previous cycle that are no longer
    // present, so a disconnected integration's server stops being lifted into the
    // serve's global config. Only prune keys we own (recorded on the sidecar);
    // never touch `augmented`, channel servers, or managed-toolkit servers.
    const fresh = new Set(Object.keys(servers));
    for (const key of previous) {
      if (!fresh.has(key)) delete mcp[key];
    }

    for (const [serverId, entry] of Object.entries(servers)) {
      mcp[serverId] = entry;
    }
    cfg['mcp'] = mcp;
    writeConfig(codeName, cfg);
    writeIntegrationServerKeys(codeName, Object.keys(servers));
  },

  removeMcpServer(codeName, serverId): void {
    const cfg = readConfig(codeName);
    const mcp = cfg['mcp'] as Record<string, unknown> | undefined;
    if (mcp && serverId in mcp) {
      delete mcp[serverId];
      writeConfig(codeName, cfg);
    }
  },

  /**
   * ENG-7994: opencode declares its servers under `mcp` in `opencode.json`, not
   * under claude-code's `mcpServers`. The manager's stale-MCP prunes used to
   * parse `mcpServers` off whatever file `getMcpPath()` returned, so for
   * opencode they iterated an empty set and never removed anything: a revoked
   * managed toolkit (prod agent nora's composio_googledocs, 2026-07-22) kept its
   * broken remote MCP entry and wedged her turns until it was deleted by hand.
   * `readConfig` already fails soft on a missing/unparseable file, so a
   * not-yet-provisioned agent reports no servers rather than throwing.
   */
  readMcpServers(codeName: string): Record<string, unknown> {
    const mcp = readConfig(codeName)['mcp'];
    // An array would survive `typeof === 'object'` and hand the prune numeric
    // index keys; only a real server map counts.
    if (!mcp || typeof mcp !== 'object' || Array.isArray(mcp)) return {};
    return mcp as Record<string, unknown>;
  },

  hasChannelCredentials(codeName, channelId): boolean {
    const mcp = readConfig(codeName)['mcp'] as Record<string, unknown> | undefined;
    return Boolean(mcp && mcp[channelId]);
  },

  /**
   * Register a channel as an opencode MCP server pointing at the shared channel
   * bundle. Credentials map to the channel server's exact env vars via
   * `buildChannelCredentialEnv` (secrets -> `.env.integrations` + `{env:VAR}`
   * refs, identifiers inline), and the full sender-gating / peer / tz env comes
   * from `buildChannelServerEnv`. Both mirror the claude-code adapter's contract
   * byte-for-byte, since the two frameworks spawn the same bundle servers.
   */
  writeChannelCredentials(codeName, channelId, config, options): void {
    const serverFile = CHANNEL_SERVER_FILES[channelId];
    if (!serverFile) return; // unknown channel — no-op
    const credential = buildChannelCredentialEnv(channelId, config);
    // Secrets are persisted out of the config file into .env.integrations.
    upsertEnvIntegrations(codeName, credential.secrets);

    const environment: Record<string, string> = {
      AGT_AGENT_CODE_NAME: codeName,
      ...credential.env, // identifiers + {env:VAR} secret refs
      ...buildChannelServerEnv(channelId, config, options as ChannelEnvOptions),
      PATH: '{env:PATH}',
      HOME: '{env:HOME}',
    };

    const cfg = readConfig(codeName);
    const mcp = (cfg['mcp'] as Record<string, unknown>) ?? {};
    mcp[channelId] = {
      type: 'local',
      command: ['node', join(getHomeDir(), '.augmented', '_mcp', serverFile)],
      environment,
      enabled: options?.addBinding !== false,
    };
    cfg['mcp'] = mcp;
    writeConfig(codeName, cfg);
  },

  /**
   * opencode has no native cron. The manager/bridge drives scheduled prompts via
   * the headless server (`session.prompt` on a timer per ADR-0047), so this
   * normalizes the enabled schedule rows into `opencode-schedules.json` for the
   * manager to read - the opencode analogue of the claude-code adapter's
   * `schedules.json`.
   */
  async syncScheduledTasks(codeName: string, tasks: ScheduledTaskRow[]): Promise<void> {
    const schedules = tasks
      .filter((t) => t.enabled)
      .map((t) => ({
        id: t.id,
        template_id: t.template_id,
        name: t.name,
        schedule: {
          kind: t.schedule_kind,
          expr: t.schedule_expr,
          every: t.schedule_every,
          at: t.schedule_at,
          tz: t.timezone,
        },
        prompt: t.prompt,
        // 'main' reuses the agent's primary conversation session; 'isolated'
        // gets a fresh opencode session per fire.
        session_target: t.session_target,
        delivery_mode: t.delivery_mode,
        delivery_policy: t.delivery_policy ?? 'always',
        delivery_channel: t.delivery_channel,
        delivery_to: t.delivery_to ?? null,
      }));
    mkdirSync(agentDir(codeName), { recursive: true });
    writeFileSync(join(agentDir(codeName), SCHEDULES_FILE), JSON.stringify({ schedules }, null, 2));
  },

  removeChannelCredentials(codeName, channelId): void {
    this.removeMcpServer?.(codeName, channelId);
  },
};

registerFramework(opencodeAdapter);

// ADR-0047 runtime driver surface. The manager (apps/cli) drives a live
// `opencode serve` over HTTP - the analogue of tmux+send-keys for Claude Code -
// but reaches this package through the single published subpath
// (`.../frameworks/opencode/index.js`). Re-export the client, the inbound
// bridge, and their contract types here so `opencode-session.ts` can import the
// runtime pieces without new package exports.
export {
  HttpOpencodeClient,
  type OpencodeClient,
  type OpencodeClientOptions,
  type OpencodeModelRef,
  type SessionMessage,
  type SessionMessagePart,
  type SessionSummary,
} from './opencode-client.js';
// ENG-7927: opencode Live View transcript model + pure builder (the manager
// writes a redacted snapshot to disk; the admin API SSM-reads it for the viewer).
export {
  buildOpencodeTranscript,
  emptyOpencodeTranscript,
  type OpencodeTranscript,
  type OpencodeTranscriptMessage,
  type OpencodeTranscriptPart,
  type OpencodeTranscriptPartKind,
} from './opencode-transcript.js';
export {
  OpencodeInboundBridge,
  InboundError,
  frameInboundPrompt,
  type BridgeOptions,
  type GateDecision,
  type InboundMessage,
  type InboundResult,
  type SenderGate,
} from './inbound-bridge.js';
// ENG-9096: the `opencode run --attach` turn driver (ENG-8032) and its pure
// helpers are GONE. It existed only because the HTTP path could not deliver MCP
// tools, and that was an `/api`-surface bug — the client now speaks `/session`,
// where tools arrive and the reply comes back on the same call. Do not
// reintroduce a subprocess-per-turn without re-measuring that surface.
// ENG-7928: the manager needs the canonical model->opencode.json transform to
// verify a provisioned model landed on disk before hot-reloading the serve.
// ENG-7931: parseOpencodeModelRef splits that model into the {providerID,id}
// shape the session APIs need. NOTE (ENG-9096): "the serve does not apply the
// config default model" was an `/api` behaviour — on `/session` a turn sent with
// no model runs opencode.json's default. The model is still passed explicitly so
// the resolved model is deterministic. The client remaps `id` -> `modelID`,
// which is what `/session` requires (sending `id` is a hard 400).
export { toOpencodeModel, parseOpencodeModelRef } from './config.js';
// ENG-7959: the manager merges the regenerated base opencode.json with the
// on-disk one so integration/channel MCP servers added incrementally aren't
// wiped on re-provision. Pure helper lives with the config builder it mirrors.
export { mergeOpencodeConfigArtifact, type OpencodeConfigMerge } from './config.js';
// ENG-7976: pure integration -> opencode `mcp` mapping (used by writeIntegrations)
// and the server-key set the manager compares to decide when an integration
// change is STRUCTURAL (warrants a serve reload) vs a credential rotation.
export {
  buildOpencodeIntegrationServers,
  opencodeIntegrationServerKeys,
  type OpencodeIntegrationServers,
} from './integrations.js';
